import {type AppBskyFeedDefs, type AppBskyFeedPost} from '@atproto/api'

import {
  TV_MASTER_PREFIX,
  TV_SEGMENT_DURATION_PREFIX,
  TV_SEGMENT_MARKER,
  TV_TOTAL_DURATION_PREFIX,
  TV_VIDEO_MARKER,
} from './tvTypes'

type PostLike = AppBskyFeedDefs.PostView | AppBskyFeedPost.Record | undefined

function getPostText(post: PostLike | {text?: string} | undefined): string {
  if (!post) return ''
  if (typeof (post as {text?: string}).text === 'string') {
    return (post as {text?: string}).text || ''
  }
  const record = (post as AppBskyFeedDefs.PostView)?.record as
    | AppBskyFeedPost.Record
    | undefined
  if (record?.text) return record.text
  const recordValue = (post as any)?.value
  if (recordValue?.text) return recordValue.text
  return ''
}

export function isTVVideoPage(post: PostLike): boolean {
  const text = getPostText(post)
  return text.includes(TV_VIDEO_MARKER)
}

export function isTVSegmentPost(post: PostLike): boolean {
  const text = getPostText(post)
  return text.includes(TV_SEGMENT_MARKER)
}

export function extractMasterUriFromVideoPage(
  post: PostLike,
): string | undefined {
  const text = getPostText(post)
  if (!text) return undefined
  const lines = text.split('\n')
  for (const line of lines) {
    const idx = line.indexOf(TV_MASTER_PREFIX)
    if (idx === -1) continue
    const raw = line.slice(idx + TV_MASTER_PREFIX.length).trim()
    if (raw.startsWith('at://')) return raw
  }
  return undefined
}

function extractPrefixedNumber(
  text: string,
  prefix: string,
): number | undefined {
  const lines = text.split('\n')
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed.startsWith(prefix)) continue
    const raw = trimmed.slice(prefix.length).trim()
    const parsed = Number(raw)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

export function extractSegmentDurationMsFromPost(
  post: PostLike,
): number | undefined {
  const text = getPostText(post)
  if (!text) return undefined
  return extractPrefixedNumber(text, TV_SEGMENT_DURATION_PREFIX)
}

export function extractTotalDurationMsFromVideoPage(
  post: PostLike,
): number | undefined {
  const text = getPostText(post)
  if (!text) return undefined
  return extractPrefixedNumber(text, TV_TOTAL_DURATION_PREFIX)
}

export function extractTitleDescFromVideoPage(post: PostLike): {
  title: string
  description?: string
} {
  const text = getPostText(post)
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .filter(
      line =>
        line !== TV_VIDEO_MARKER &&
        line !== TV_SEGMENT_MARKER &&
        !line.startsWith(TV_MASTER_PREFIX) &&
        !line.startsWith(TV_SEGMENT_DURATION_PREFIX) &&
        !line.startsWith(TV_TOTAL_DURATION_PREFIX),
    )
  const title = lines[0] || ''
  const description = lines.slice(1).join('\n').trim()
  return {title, description: description || undefined}
}
